import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnVisibilityState } from '@tanstack/react-table';

import {
    DataTable,
    type DataTableRow,
    type DataTableRowProps,
} from '@/components/organisms/DataTable';
import type { ClientSummary } from '@/domain/client';

import { PAGE_SIZE_OPTIONS } from '../clientListColumns';
import { compareClientsByDefault } from '../clientStatus';

import ClientContractsPanel from './ClientContractsPanel';
import { clientUrl, useClientTableColumns } from './useClientTableColumns';

import styles from './ClientTable.module.scss';

interface ClientTableProps {
    clients: ClientSummary[];
    filter: string;
    emptyState: string;
    columnVisibility: ColumnVisibilityState;
    onColumnVisibilityChange: (visibility: ColumnVisibilityState) => void;
    pageSize: number;
    onPageSizeChange: (pageSize: number) => void;
}

export default function ClientTable({
    clients,
    filter,
    emptyState,
    columnVisibility,
    onColumnVisibilityChange,
    pageSize,
    onPageSizeChange,
}: ClientTableProps) {
    const navigate = useNavigate();
    const columns = useClientTableColumns();
    // Opens in this order; clicking a header sorts by that column instead.
    const ordered = useMemo(() => [...clients].sort(compareClientsByDefault), [clients]);

    const rowProps = (row: DataTableRow<ClientSummary>): DataTableRowProps => ({
        className: styles.clientRow,
        variant: row.original.archived ? 'inactive' : 'default',
        onClick: () => navigate(clientUrl(row.original.id)),
    });

    return (
        <DataTable
            responsive
            data={ordered}
            columns={columns}
            getRowId={(client) => String(client.id)}
            filter={filter}
            enablePagination
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={onPageSizeChange}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            enableColumnSelector={false}
            getRowProps={rowProps}
            getRowCanExpand={(row) => row.original.totalContractCount > 0}
            renderExpanded={(row) => <ClientContractsPanel clientId={row.original.id} />}
            emptyState={emptyState}
        />
    );
}
